/** React hooks for cs-api-backed workshop registration. */
import { useCallback, useEffect, useState } from 'react'

import useSWR from 'swr'

/**
 * HTTP client for the **cs-api** registration service (workshops, waitlists, payments, SSE).
 * Base URL: `NEXT_PUBLIC_WORKSHOP_SERVICE_URL` (origin only; routes are under `/api/v1`).
 */

// Types for workshop registration
export interface WorkshopPreference {
  workshopInstanceId: string
  preference: number // 1 = highest
  format: 'in-person' | 'online'
  title: string
}

export interface UserProfile {
  name: string
  phone?: string
  company?: string
  jobTitle?: string
  city?: string
  country?: string
  marketingConsent?: boolean
  paymentPreference?: 'stripe' | 'xero'
}

export interface Registration {
  id: string
  userId: string
  workflowExecutionId: string
  currentState: string
  contextData: any
  selectedWorkshops: WorkshopPreference[]
  assignedWorkshopInstanceId?: string
  formatPreference: 'in-person' | 'online'
  registrationSource: string
  createdAt: string
  updatedAt: string
}

export interface Workshop {
  id: string
  title: string
  description: string
  category: string
  difficultyLevel: 'beginner' | 'intermediate' | 'advanced'
  durationHours: number
  inPersonCapacity: number
  onlineCapacity: number
  inPersonPrice: number
  onlinePrice: number
  location?: string
  status: 'draft' | 'published' | 'cancelled'
}

export interface WorkshopInstance {
  id: string
  workshopId: string
  startDate: string
  endDate: string
  timezone: string
  format: 'in-person' | 'online' | 'hybrid'
  currentInPersonSpots: number
  currentOnlineSpots: number
  maxInPersonSpots: number
  maxOnlineSpots: number
  status: 'scheduled' | 'running' | 'completed' | 'cancelled'
  meetingLink?: string
  workshop?: Workshop
}

export interface AvailabilityCheck {
  workshopInstanceId: string
  format: 'in-person' | 'online'
  spotsAvailable: number
  totalCapacity: number
  currentRegistrations: number
  waitlistLength: number
}

export interface WaitlistStatus {
  id: string
  position: number
  workshopInstanceId: string
  format: 'in-person' | 'online'
  status: 'active' | 'offered' | 'expired' | 'converted'
  spotOfferedAt?: string
  spotExpiresAt?: string
}

export interface PaymentIntent {
  clientSecret: string
  amount: number
  currency: string
  registrationId: string
}

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: {
    code: string
    message: string
    details?: any
  }
  metadata?: {
    timestamp: string
    requestId: string
  }
}

/** HTTP client for cs-api (`/api/v1/...`). */
export class WorkshopApiClient {
  private baseUrl: string
  private apiKey?: string

  constructor() {
    // Use environment variable for service URL, fallback to localhost for development
    this.baseUrl = process.env.NEXT_PUBLIC_WORKSHOP_SERVICE_URL || 'http://localhost:3001'
    this.apiKey = process.env.NEXT_PUBLIC_WORKSHOP_API_KEY
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}/api/v1${endpoint}`

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    if (this.apiKey) {
      ;(headers as Record<string, string>)['Authorization'] = `Bearer ${this.apiKey}`
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      })

      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: data.error?.code || 'API_ERROR',
            message: data.error?.message || 'Request failed',
            details: data.error?.details,
          },
        }
      }

      return {
        success: true,
        data: data.data || data,
        metadata: data.metadata,
      }
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network request failed',
        },
      }
    }
  }

  /**
   * Workshop Discovery
   */
  async getWorkshops(filters?: {
    category?: string
    difficulty?: string
    format?: string
  }): Promise<ApiResponse<Workshop[]>> {
    const params = new URLSearchParams()
    if (filters?.category) params.append('category', filters.category)
    if (filters?.difficulty) params.append('difficulty', filters.difficulty)
    if (filters?.format) params.append('format', filters.format)

    return this.request(`/workshops?${params.toString()}`)
  }

  async getWorkshopInstances(workshopId?: string): Promise<ApiResponse<WorkshopInstance[]>> {
    const endpoint = workshopId ? `/workshops/${workshopId}/instances` : '/workshop-instances'
    return this.request(endpoint)
  }

  async getWorkshopAvailability(
    workshopInstanceId: string,
    format: 'in-person' | 'online'
  ): Promise<ApiResponse<AvailabilityCheck>> {
    return this.request(`/workshops/${workshopInstanceId}/availability?format=${format}`)
  }

  /**
   * Registration Management
   */
  async startRegistration(data: {
    email: string
    preferences: WorkshopPreference[]
    userProfile: UserProfile
  }): Promise<ApiResponse<Registration>> {
    return this.request('/registrations', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getRegistration(registrationId: string): Promise<ApiResponse<Registration>> {
    return this.request(`/registrations/${registrationId}`)
  }

  async updateRegistrationPreferences(
    registrationId: string,
    preferences: WorkshopPreference[]
  ): Promise<ApiResponse<Registration>> {
    return this.request(`/registrations/${registrationId}/preferences`, {
      method: 'PUT',
      body: JSON.stringify({ preferences }),
    })
  }

  async cancelRegistration(
    registrationId: string,
    reason?: string
  ): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(`/registrations/${registrationId}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    })
  }

  /**
   * Payment Processing
   */
  async createPaymentIntent(
    registrationId: string,
    workshopInstanceId: string,
    paymentMethod: 'stripe' | 'xero' = 'stripe'
  ): Promise<ApiResponse<PaymentIntent>> {
    return this.request('/payments/intent', {
      method: 'POST',
      body: JSON.stringify({
        registrationId,
        workshopInstanceId,
        paymentMethod,
      }),
    })
  }

  async confirmPayment(
    registrationId: string,
    paymentIntentId: string
  ): Promise<ApiResponse<{ success: boolean }>> {
    return this.request('/payments/confirm', {
      method: 'POST',
      body: JSON.stringify({
        registrationId,
        paymentIntentId,
      }),
    })
  }

  /**
   * Waitlist Management
   */
  async joinWaitlist(
    registrationId: string,
    preferences: WorkshopPreference[]
  ): Promise<ApiResponse<WaitlistStatus[]>> {
    return this.request('/waitlist', {
      method: 'POST',
      body: JSON.stringify({
        registrationId,
        preferences,
      }),
    })
  }

  async getWaitlistStatus(registrationId: string): Promise<ApiResponse<WaitlistStatus[]>> {
    return this.request(`/waitlist/${registrationId}`)
  }

  async acceptSpotOffer(
    waitlistEntryId: string,
    paymentMethod: 'stripe' | 'xero' = 'stripe'
  ): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(`/waitlist/${waitlistEntryId}/accept`, {
      method: 'POST',
      body: JSON.stringify({ paymentMethod }),
    })
  }

  async declineSpotOffer(waitlistEntryId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(`/waitlist/${waitlistEntryId}/decline`, {
      method: 'POST',
    })
  }

  /**
   * Real-time Updates
   */
  createRegistrationStream(registrationId: string): EventSource {
    let url = `${this.baseUrl}/api/v1/registrations/${registrationId}/stream`
    // EventSource cannot send Authorization; backend expects ?token= when WORKSHOP_API_KEY is set.
    if (this.apiKey) {
      url += `?token=${encodeURIComponent(this.apiKey)}`
    }
    return new EventSource(url)
  }

  createWaitlistStream(registrationId: string): EventSource {
    let url = `${this.baseUrl}/api/v1/waitlist/${registrationId}/stream`
    if (this.apiKey) {
      url += `?token=${encodeURIComponent(this.apiKey)}`
    }
    return new EventSource(url)
  }

  /**
   * Health Check
   */
  async healthCheck(): Promise<ApiResponse<{ status: string }>> {
    return this.request('/health')
  }
}

const apiClient = new WorkshopApiClient()

// Hook for fetching workshops
export function useWorkshops(filters?: {
  category?: string
  difficulty?: string
  format?: string
}) {
  const { data, error, mutate } = useSWR(
    ['workshops', filters],
    () => apiClient.getWorkshops(filters),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  )

  return {
    workshops: data?.data || [],
    loading: !data && !error,
    error: data?.error || error,
    refetch: mutate,
  }
}

// Hook for workshop instances
export function useWorkshopInstances(workshopId?: string) {
  const { data, error, mutate } = useSWR(
    ['workshop-instances', workshopId],
    () => apiClient.getWorkshopInstances(workshopId),
    {
      revalidateOnFocus: false,
      refreshInterval: 30000, // Refresh every 30 seconds for availability
    }
  )

  return {
    instances: data?.data || [],
    loading: !data && !error,
    error: data?.error || error,
    refetch: mutate,
  }
}

// Hook for registration management
export function useRegistration(registrationId?: string) {
  const [registration, setRegistration] = useState<Registration | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const startRegistration = useCallback(
    async (data: {
      email: string
      preferences: WorkshopPreference[]
      userProfile: UserProfile
    }) => {
      setLoading(true)
      setError(null)

      const response = await apiClient.startRegistration(data)

      if (response.success && response.data) {
        setRegistration(response.data)
      } else {
        setError(response.error?.message || 'Registration failed')
      }

      setLoading(false)
      return response
    },
    []
  )

  const updatePreferences = useCallback(
    async (preferences: WorkshopPreference[]) => {
      if (!registrationId) return

      setLoading(true)
      const response = await apiClient.updateRegistrationPreferences(registrationId, preferences)

      if (response.success && response.data) {
        setRegistration(response.data)
      } else {
        setError(response.error?.message || 'Update failed')
      }

      setLoading(false)
      return response
    },
    [registrationId]
  )

  const cancelRegistration = useCallback(
    async (reason?: string) => {
      if (!registrationId) return

      setLoading(true)
      const response = await apiClient.cancelRegistration(registrationId, reason)
      setLoading(false)
      return response
    },
    [registrationId]
  )

  // Fetch registration data on mount
  useEffect(() => {
    if (registrationId) {
      setLoading(true)
      apiClient.getRegistration(registrationId).then(response => {
        if (response.success && response.data) {
          setRegistration(response.data)
        } else {
          setError(response.error?.message || 'Failed to load registration')
        }
        setLoading(false)
      })
    }
  }, [registrationId])

  return {
    registration,
    loading,
    error,
    startRegistration,
    updatePreferences,
    cancelRegistration,
  }
}

// Hook for real-time registration updates
export function useRegistrationUpdates(registrationId?: string) {
  const [registration, setRegistration] = useState<Registration | null>(null)
  const [connected, setConnected] = useState(false)

  useEffect(() => {
    if (!registrationId) return

    const eventSource = apiClient.createRegistrationStream(registrationId)

    eventSource.onopen = () => {
      setConnected(true)
    }

    eventSource.onmessage = event => {
      try {
        const data = JSON.parse(event.data)
        setRegistration(data)
      } catch (error) {
        console.error('Failed to parse registration update:', error)
      }
    }

    eventSource.onerror = () => {
      setConnected(false)
    }

    return () => {
      eventSource.close()
      setConnected(false)
    }
  }, [registrationId])

  return {
    registration,
    connected,
  }
}

// Export singleton instance
export const workshopApi = new WorkshopApiClient()
export default workshopApi
